import React, { useMemo, useState } from 'react';
import {
  Container,
  ContainerProps,
  BoxProps,
  styled,
  Paper,
} from '@mui/material';
import MerchandiserSubheader from './MerchandiserSubheader';
import MerchandiserSettings from './MerchandiserSettings';
import MerchandiserMonitoring from './MerchandiserMonitoring';
import MerchandiserIncidentListPage from './MerchandiserIncidentListPage';
import MerchandiserDetail from './MerchandiserDetail';
import { ParamsProvider } from '../../../../util/context/ParamsProvider';
import MerchandiserReportsPage from './MerchandiserReportsPage';
import { Merchandiser } from '../../redux/composableDevice/model/Merchandiser.model';
import { useGetValidControlUnits } from '../../hooks/useGetValidControlUnits';

const MainContainer = (props: { moduleSelected: string } & BoxProps) => {
  const { children } = props;

  return (
    <Container
      maxWidth="xl"
      disableGutters
      sx={{
        flexDirection: 'column',
        display: 'flex',
      }}
    >
      {children}
    </Container>
  );
};

const ChildContainer = (props: ContainerProps) => {
  const { children, ...others } = props;

  return (
    <Container
      disableGutters
      sx={{
        py: 1,
        px: { xs: 1, md: 4 },
        backgroundColor: 'background.container',
        flexGrow: 1,
        display: 'flex',
        gap: '2rem',
      }}
      {...others}
      maxWidth="xl"
    >
      {children}
    </Container>
  );
};
const VIEWS = (merchandiser: any) => {
  return {
    detail: <MerchandiserDetail merchandiser={merchandiser} />,
    settings: <MerchandiserSettings />,
    history: <MerchandiserMonitoring />,
    alarms: <MerchandiserIncidentListPage />,
    reports: <MerchandiserReportsPage />,
  };
};

export type ViewKey = 'settings' | 'detail' | 'history' | 'alarms' | 'reports';

export const ContentPage = styled(Paper)`
  border-radius: 1rem;
  padding: 0.4rem;
`;
interface MerchandiserContainerProps {
  merchandiser: Merchandiser;
  collapsed: boolean;
  onCollapse: (merchandiserId: string) => void;
}
export function MerchandiserContainer({
  merchandiser,
  collapsed,
  onCollapse,
}: MerchandiserContainerProps) {
  const [selectedView, setSelectedView] = useState<ViewKey>('detail');
  const view = VIEWS(merchandiser)[selectedView];
  const params = useMemo(
    () => ({
      groupId: merchandiser.id,
      merchandiserId: merchandiser.deviceId,
    }),
    [merchandiser.id, merchandiser.deviceId]
  );
  const validControlUnits = useGetValidControlUnits(merchandiser);

  return (
    <ParamsProvider params={params}>
      <MainContainer moduleSelected={selectedView}>
        {merchandiser && (
          <MerchandiserSubheader
            merchandiserId={merchandiser.id}
            merchandiser={merchandiser}
            moduleSelected={collapsed ? 'detail' : selectedView}
            onClick={(key) => {
              if (collapsed) onCollapse(merchandiser.id);
              setSelectedView(key);
            }}
            key={merchandiser.id}
            updateCollapse={() => {
              if (
                !validControlUnits ||
                validControlUnits.length === 0 ||
                !merchandiser.channels?.length
              ) {
                return;
              }
              onCollapse(merchandiser.id);
            }}
            collapsed={collapsed}
          />
        )}
        {!collapsed ? <ChildContainer>{view}</ChildContainer> : null}
      </MainContainer>
    </ParamsProvider>
  );
}

export default MerchandiserContainer;
